import { createElement, Fragment, type ReactNode } from "react";

export type Replacements = Record<string, string | number | ReactNode>;

/**
 * Replaces placeholders like {{key}} in a string with values from `replacements`.
 * Keeps spaces exactly as in the original string.
 *
 * Examples:
 *
 * // Only string → returns string
 * interpolateText("Hello {{name}}!", { name: "John" })
 * // -> "Hello John!"
 *
 * // Number → returns string
 * interpolateText("You saved {{amount}}%", { amount: 20 })
 * // -> "You saved 20%"
 *
 * // JSX → returns keyed fragment children (no bare array)
 * interpolateText("Was {{price}}", { price: <Money data={{ centAmount: 1000, currencyCode: 'DKK' }} /> })
 * // -> <>…</> wrapping "Was " and <Money />
 *
 * // Mixed → same (fragment with stable keys per segment)
 * interpolateText(
 *   "Hello {{name}}, balance {{balance}}, offer {{offer}}",
 *   { name: "John", balance: 50, offer: <Money data={{ centAmount: 2000, currencyCode: "DKK" }} /> }
 * )
 */
export const interpolateText = (text: string, replacements: Replacements): string | ReactNode => {
    // Start with the full text as a single element
    const parts: ReactNode[] = [text];

    Object.entries(replacements).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];

            if (typeof part !== "string" || !part.includes(placeholder)) continue;

            // Split the string around the placeholder
            const split = part.split(placeholder);

            // Keep spaces before/after placeholder intact
            const mapped = split.flatMap(
                (segment, index) =>
                    index < split.length - 1
                        ? [
                              segment, // text before placeholder
                              typeof value === "number" ? String(value) : value, // inserted replacement
                          ]
                        : [segment] // last segment
            );

            parts.splice(i, 1, ...mapped);
        }
    });

    // If all parts are strings → join as a single string
    if (parts.every((p) => typeof p === "string")) {
        return parts.join("");
    }

    // If only one element → return it directly
    if (parts.length === 1) {
        return parts[0];
    }

    // Keyed fragments: bare arrays trigger React "unique key" warnings when rendered as children
    // (use createElement — this file is .ts, not .tsx)
    return createElement(
        Fragment,
        null,
        ...parts.map((part, index) =>
            createElement(Fragment, { key: `interpolate-${index}` }, part)
        )
    );
};
